import { useState } from 'react';
import { SCENARIOS } from '@/content/solutions';
import { SHOT_SIZES_FULL, type ShotId } from '@/content/shots';
import { Icon } from './Icon';
import { Screenshot } from './Screenshot';
import { Section, SectionHeading } from './Section';
import { Reveal } from './Reveal';

/** 每个场景配一张最贴近的界面截图；四张图在五个场景里复用。 */
const SCENARIO_SHOT: Record<string, ShotId> = {
  park: 'fleet-overview',
  inspection: 'single-drone-realtime',
  research: 'alert-thresholds',
  diy: 'single-drone-realtime',
  enterprise: 'audit-log',
};

/**
 * 场景演示：左侧一列可切换的场景，右侧跟着换的界面截图。
 *
 * 这是阿里云「解决方案」区块的版式 —— 用界面本身来说明场景，
 * 比再写一段形容词更省字数。左侧展开项同时充当详情入口。
 */
export function SolutionsShowcase() {
  const [activeId, setActiveId] = useState<string>(SCENARIOS[0]?.id ?? '');
  const active = SCENARIOS.find((scenario) => scenario.id === activeId) ?? SCENARIOS[0];

  if (!active) return null;

  const shot = SCENARIO_SHOT[active.id] ?? 'fleet-overview';

  return (
    <Section tone="raised" id="showcase">
      <SectionHeading
        eyebrow="场景演示"
        title="先看界面，再说场景"
        lead="切换左侧的场景，右侧会换成该场景下最常盯的那一屏。所有截图都来自演示环境，不是设计稿。"
        align="center"
      />

      <div className="mt-12 grid grid-cols-[minmax(0,1fr)] gap-8 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] lg:items-start lg:gap-12">
        <div className="flex min-w-0 flex-col border-t border-line">
          {SCENARIOS.map((scenario) => {
            const isActive = scenario.id === active.id;
            return (
              <div key={scenario.id} className="border-b border-line">
                <button
                  type="button"
                  onClick={() => setActiveId(scenario.id)}
                  aria-expanded={isActive}
                  className="flex w-full items-center gap-3 px-1 py-4 text-left transition-colors hover:bg-surface-2/60"
                >
                  <Icon
                    name={scenario.icon}
                    size={18}
                    className={isActive ? 'text-brand' : 'text-fg-subtle'}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={['block text-[0.9375rem] font-medium', isActive ? 'text-brand' : 'text-fg'].join(' ')}>
                      {scenario.title}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-fg-muted">{scenario.audience}</span>
                  </span>
                  <Icon
                    name="chevronDown"
                    size={15}
                    className={['shrink-0 text-fg-subtle transition-transform', isActive ? 'rotate-180' : ''].join(' ')}
                  />
                </button>

                {isActive ? (
                  <div className="flex flex-col gap-3 px-1 pb-5">
                    <p className="text-sm leading-relaxed text-fg-muted">{scenario.situation}</p>
                    <div className="flex flex-wrap gap-2">
                      {scenario.outcomes.map((outcome) => (
                        <span
                          key={outcome}
                          className="inline-flex items-center gap-1.5 rounded-md bg-grad-tint px-2.5 py-1 text-xs font-medium text-brand"
                        >
                          <Icon name="check" size={12} />
                          {outcome}
                        </span>
                      ))}
                    </div>
                    <a
                      href={`#${scenario.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand transition-colors hover:text-brand-hover"
                    >
                      查看该场景的完整说明
                      <Icon name="arrowRight" size={14} />
                    </a>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <Reveal className="min-w-0 lg:sticky lg:top-24">
          <Screenshot id={shot} sizes={SHOT_SIZES_FULL} scrollOnSmall />
        </Reveal>
      </div>
    </Section>
  );
}
